import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExamTypeToggle from "@/components/ExamTypeToggle";
import { LineChart as LineIcon, Trophy, Users, Target, Gauge, FileSpreadsheet, ArrowUp, ArrowDown } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";

const NET = "#10b981";
const SCORE = "#6366f1";

const pct = (cur, prev) => (prev == null || prev === 0 ? null : ((cur - prev) / Math.abs(prev)) * 100);

function Delta({ v }) {
  if (v == null) return <span className="text-xs text-muted-foreground ml-1.5">—</span>;
  const up = v >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ml-1.5 font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`} title={up ? "önceki KTT'ye göre yükseldi" : "önceki KTT'ye göre düştü"}>
      <Icon className="h-3 w-3" />%{Math.abs(v).toFixed(1)}
    </span>
  );
}

function Stat({ icon: Icon, label, value, sub, tid }) {
  return (
    <Card className="p-4 bg-card border-border" data-testid={tid}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-1 text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function KttAnalytics() {
  const [data, setData] = useState(null);
  const [grade, setGrade] = useState("all");
  const [grades, setGrades] = useState([]);
  const [examType, setExamType] = useState("KTT");
  const typeLabel = examType === "KTT" ? "KTT" : "Deneme";

  useEffect(() => {
    const params = { exam_type: examType };
    if (grade !== "all") params.grade = grade;
    api.get("/analytics/overview", { params })
      .then((r) => { setData(r.data); if (r.data.grades?.length) setGrades(r.data.grades); })
      .catch((e) => toast.error(apiError(e)));
  }, [grade, examType]);

  const s = data?.summary;
  const hasData = data && data.points?.length > 0;

  return (
    <div>
      <PageHeader title="Sınav Analizi" desc="Konu Tarama Testleri ve Denemelerin net ve puan analizi">
        <div className="flex flex-wrap items-center gap-3">
          <ExamTypeToggle value={examType} onChange={setExamType} tid="analytics-type" />
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Sınıf</Label>
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="w-32" data-testid="analytics-grade"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Tüm Sınıflar</SelectItem>
                {grades.map((g) => <SelectItem key={g} value={g}>{g}. Sınıf</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PageHeader>

      {!hasData ? (
        <EmptyState icon={LineIcon} title="Analiz için veri yok" desc={`Henüz sonucu okunmuş bir ${typeLabel} bulunmuyor. Optik okutma yaptıkça buradaki grafikler dolacak.`} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat icon={FileSpreadsheet} label={`${typeLabel} Sayısı`} value={s.exam_count} tid="stat-exams" />
            <Stat icon={Users} label="Değerlendirilen" value={s.result_count} sub="öğrenci okuması" tid="stat-results" />
            <Stat icon={Target} label="Ortalama Net" value={s.avg_net} tid="stat-net" />
            <Stat icon={Gauge} label="Ortalama Puan" value={s.avg_score} sub="500 üzerinden" tid="stat-score" />
            <Stat icon={Trophy} label={`En İyi ${typeLabel}`} value={s.best?.avg_net ?? "—"} sub={s.best?.name} tid="stat-best" />
          </div>

          <Card className="p-5 bg-card border-border overflow-x-auto" data-testid="ktt-table">
            <h3 className="font-semibold mb-1">{typeLabel} Bazında Net ve Puan</h3>
            <p className="text-xs text-muted-foreground mb-4">Yüzdelik değişim bir önceki {typeLabel}'ye göredir</p>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">KTT</th>
                <th className="py-2 px-4 font-medium">Tarih</th>
                <th className="py-2 px-4 font-medium text-right">Öğrenci</th>
                <th className="py-2 px-4 font-medium text-right">Ort. Net</th>
                <th className="py-2 pl-4 font-medium text-right">Ort. Puan</th>
              </tr></thead>
              <tbody>
                {data.points.map((p, i) => {
                  const prev = i > 0 ? data.points[i - 1] : null;
                  const dn = prev ? pct(p.avg_net, prev.avg_net) : null;
                  const ds = prev ? pct(p.avg_score, prev.avg_score) : null;
                  return (
                    <tr key={p.exam_id} className="border-b border-border/50" data-testid={`ktt-row-${p.exam_id}`}>
                      <td className="py-2.5 pr-4 font-medium">{p.name}</td>
                      <td className="py-2.5 px-4 text-muted-foreground">{p.date}</td>
                      <td className="py-2.5 px-4 text-right">{p.count}</td>
                      <td className="py-2.5 px-4 text-right whitespace-nowrap"><span className="font-semibold" style={{ color: NET }}>{p.avg_net}</span><Delta v={dn} /></td>
                      <td className="py-2.5 pl-4 text-right whitespace-nowrap"><span className="font-semibold" style={{ color: SCORE }}>{p.avg_score}</span><Delta v={ds} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Card className="p-5 bg-card border-border" data-testid="chart-trend">
            <h3 className="font-semibold mb-4">KTT Bazında Ortalama Net ve Puan</h3>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data.points} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.4} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-12} textAnchor="end" height={50} interval={0} />
                <YAxis yAxisId="net" orientation="left" tick={{ fontSize: 11 }} stroke={NET} />
                <YAxis yAxisId="score" orientation="right" tick={{ fontSize: 11 }} stroke={SCORE} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb" }} />
                <Legend />
                <Line yAxisId="net" type="monotone" dataKey="avg_net" name="Ort. Net" stroke={NET} strokeWidth={2.5} dot={{ r: 3 }} />
                <Line yAxisId="score" type="monotone" dataKey="avg_score" name="Ort. Puan" stroke={SCORE} strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-5 bg-card border-border" data-testid="chart-subjects">
            <h3 className="font-semibold mb-4">Ders Bazında Ortalama Net</h3>
            <ResponsiveContainer width="100%" height={Math.max(220, data.subjects.length * 44)}>
              <BarChart data={data.subjects} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.4} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb" }} />
                <Bar dataKey="avg_net" name="Ort. Net" radius={[0, 6, 6, 0]}>
                  {data.subjects.map((_, i) => <Cell key={i} fill={`hsl(${160 + i * 24}, 60%, 48%)`} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {grade === "all" ? (
            <Card className="p-4 bg-muted/40 border-border text-sm text-muted-foreground" data-testid="branch-hint">
              Şube karşılaştırması (ör. 6-A vs 6-B) için yukarıdan bir sınıf seçin.
            </Card>
          ) : (() => {
            const brs = (data.branches || []).filter((b) => b.grade === grade);
            if (brs.length === 0) return null;
            return (
              <Card className="p-5 bg-card border-border" data-testid="chart-branches">
                <h3 className="font-semibold mb-1">Şube Karşılaştırması — {grade}. Sınıf</h3>
                <p className="text-xs text-muted-foreground mb-4">Tüm KTT'ler ortalaması{brs.length < 2 ? " · bu sınıfta tek şube var" : ""}</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={brs} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="net" orientation="left" tick={{ fontSize: 11 }} stroke={NET} />
                    <YAxis yAxisId="score" orientation="right" tick={{ fontSize: 11 }} stroke={SCORE} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb" }} />
                    <Legend />
                    <Bar yAxisId="net" dataKey="avg_net" name="Ort. Net" fill={NET} radius={[6, 6, 0, 0]} />
                    <Bar yAxisId="score" dataKey="avg_score" name="Ort. Puan" fill={SCORE} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            );
          })()}
        </div>
      )}
    </div>
  );
}
